import type * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Alert from '@cloudscape-design/components/alert';
import Badge from '@cloudscape-design/components/badge';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ButtonDropdown from '@cloudscape-design/components/button-dropdown';
import ContentLayout from '@cloudscape-design/components/content-layout';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Table from '@cloudscape-design/components/table';
import Toggle from '@cloudscape-design/components/toggle';
import type { FinanceAccount } from '@etiquetachef/types';
import { FinanceAccountId } from '@etiquetachef/types';
import {
  subscribeFinanceAccounts,
  type FinanceAccountListResult,
} from '@/lib/firestoreFinance';
import { archiveFinanceAccountCallable } from '@/lib/callables';
import { toast } from '@/lib/notifications';
import { formatCurrencyCents } from '@/features/dashboard-finance/formatters';
import { FinanceAccountEditorDialog } from '@/features/finance-accounts/FinanceAccountEditorDialog';

/**
 * Tela `/finance/accounts`: contas do razão interno (banco/caixa), com
 * criar, editar e arquivar.
 *
 * Origem: #73 — 2026-06-21 19:44 +0200 (módulo Financeiro interno — menu,
 * Contas e Origens).
 *
 * Por quê: conta arquivada não some (os lançamentos antigos apontam para
 * ela); o toggle "mostrar arquivadas" filtra no cliente.
 *
 * Contrato: lista por `subscribeFinanceAccounts` (listener). Mutações só
 * por callable, todas com capability `manageFinance` (ROOT/FINANCE,
 * `docs/permissions.md`): `createFinanceAccount`/`updateFinanceAccount`
 * (no `FinanceAccountEditorDialog`) e `archiveFinanceAccount` (alterna
 * `archived`; sucesso/erro viram toast). OPS/SUPPORT veem a lista e
 * recebem `permission-denied` ao mutar.
 *
 * Usado por: `routes/router.tsx` (rota `finance/accounts`, dentro do
 * `RequireSuperAdmin`), menu Financeiro → "Contas" (`AdminLayout`). Só
 * `apps/admin` (web). Spec: `docs/screens/finance-accounts-admin.md`.
 *
 * Arquitetura: regra `match /finance_accounts/{accountId}` (Lê: super-admin;
 * Escreve: só servidor). Coleção root, fora do escopo de tenant — é o
 * caixa da própria empresa, não de cliente.
 *
 * Dados: `finance_accounts/{id}` (`FinanceAccountSchema`) — exemplo em
 * `subscribeFinanceAccounts`. `openingBalanceCents` em CENTAVOS (pode ser
 * negativo), exibido por `formatCurrencyCents` na moeda da conta.
 *
 * Issues: #73 (origem).
 *
 * Histórico:
 *  - 2026-10-10 14:53 +0200 — (#798) cabeçalho padrão (#796).
 */
export function FinanceAccountsPage(): React.JSX.Element {
  const { t } = useTranslation();
  const [state, setState] = useState<FinanceAccountListResult>({ kind: 'LOADING' });
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<FinanceAccount | null>(null);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    setState({ kind: 'LOADING' });
    return subscribeFinanceAccounts(setState);
  }, []);

  const items = useMemo(() => {
    if (state.kind !== 'READY') return [];
    return showArchived ? state.accounts : state.accounts.filter((a) => !a.archived);
  }, [state, showArchived]);

  /** Arquiva/desarquiva pela callable `archiveFinanceAccount`; resultado em toast. */
  async function toggleArchive(account: FinanceAccount): Promise<void> {
    try {
      await archiveFinanceAccountCallable({
        accountId: FinanceAccountId(account.id),
        archived: !account.archived,
      });
      toast.success(
        t(
          account.archived
            ? 'admin.finance.accounts.success.unarchived'
            : 'admin.finance.accounts.success.archived',
        ),
      );
    } catch (err) {
      console.error('toggleArchive account failed', err);
      toast.error(t('admin.finance.actionFailed'));
    }
  }

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={t('admin.finance.accounts.subtitle')}
          actions={
            <Button
              variant="primary"
              onClick={(): void => {
                setCreating(true);
              }}
            >
              {t('admin.finance.accounts.create')}
            </Button>
          }
        >
          {t('admin.finance.accounts.title')}
        </Header>
      }
    >
      <SpaceBetween size="m">
        {state.kind === 'ERROR' && <Alert type="error">{t('admin.finance.accounts.errorLoad')}</Alert>}
        <Table<FinanceAccount>
          items={items}
          loading={state.kind === 'LOADING'}
          trackBy="id"
          variant="container"
          resizableColumns
          stickyHeader
          filter={
            <Toggle
              checked={showArchived}
              onChange={({ detail }): void => {
                setShowArchived(detail.checked);
              }}
            >
              {t('admin.finance.showArchived')}
            </Toggle>
          }
          empty={
            <Box textAlign="center" color="text-status-inactive" padding="m">
              {t('admin.finance.accounts.empty')}
            </Box>
          }
          columnDefinitions={[
            {
              id: 'name',
              header: t('admin.finance.accounts.columns.name'),
              cell: (a): React.ReactNode => a.name,
            },
            {
              id: 'bank',
              header: t('admin.finance.accounts.columns.bank'),
              cell: (a): React.ReactNode => a.bank ?? '—',
            },
            {
              id: 'kind',
              header: t('admin.finance.accounts.columns.kind'),
              cell: (a): React.ReactNode => t(`admin.finance.accounts.kind.${a.kind}`),
            },
            {
              id: 'currency',
              header: t('admin.finance.accounts.columns.currency'),
              cell: (a): React.ReactNode => a.currency,
            },
            {
              id: 'timeZone',
              header: t('admin.finance.accounts.columns.timeZone'),
              cell: (a): React.ReactNode => a.timeZone,
            },
            {
              id: 'openingBalance',
              header: t('admin.finance.accounts.columns.openingBalance'),
              cell: (a): React.ReactNode => formatCurrencyCents(a.openingBalanceCents, a.currency),
            },
            {
              id: 'status',
              header: t('admin.finance.accounts.columns.status'),
              cell: (a): React.ReactNode =>
                a.archived ? (
                  <Badge color="grey">{t('admin.finance.status.archived')}</Badge>
                ) : (
                  <Badge color="green">{t('admin.finance.status.active')}</Badge>
                ),
            },
            {
              id: 'actions',
              header: t('admin.finance.accounts.columns.actions'),
              minWidth: 90,
              cell: (a): React.ReactNode => (
                <ButtonDropdown
                  variant="icon"
                  ariaLabel={t('admin.finance.actions.menu')}
                  expandToViewport
                  items={[
                    { id: 'edit', text: t('admin.finance.actions.edit') },
                    {
                      id: 'archive',
                      text: t(
                        a.archived
                          ? 'admin.finance.actions.unarchive'
                          : 'admin.finance.actions.archive',
                      ),
                    },
                  ]}
                  onItemClick={({ detail }): void => {
                    if (detail.id === 'edit') setEditing(a);
                    else if (detail.id === 'archive') void toggleArchive(a);
                  }}
                />
              ),
            },
          ]}
        />
      </SpaceBetween>

      <FinanceAccountEditorDialog
        open={creating || editing !== null}
        mode={editing ? 'edit' : 'create'}
        account={editing}
        onClose={(): void => {
          setCreating(false);
          setEditing(null);
        }}
      />
    </ContentLayout>
  );
}
